import { useState } from "react";
import { LayoutGroup, motion } from "framer-motion";
import {
  BookOpen,
  Handshake,
  Repeat,
  Search,
  ShieldCheck,
  TrendingUp,
  BadgeCheck,
} from "lucide-react";
import {
  CONSUMER_ENABLE,
  INSTITUTION_ENABLE,
  JOURNEY_PHASES,
  JOURNEY_STEPS,
  PRODUCTS,
} from "../../constants/about";
import { ArrowIcon, EASE, MediaSlot, useFade } from "./ui";

const STEP_ICONS = {
  search: Search,
  book: BookOpen,
  handshake: Handshake,
  shield: ShieldCheck,
  check: BadgeCheck,
  repeat: Repeat,
  trend: TrendingUp,
};

function AboutPlatform() {
  const { fade, reduce } = useFade();
  const [step, setStep] = useState(0);

  return (
    <>
      <section
        id="what-we-do"
        className="relative scroll-mt-32 overflow-hidden bg-white text-navy"
        aria-labelledby="what-heading"
      >
        <div className="pointer-events-none absolute inset-0" aria-hidden="true">
          <div className="absolute inset-0 bg-[radial-gradient(70%_50%_at_0%_0%,rgba(255,191,24,0.1),transparent_55%),radial-gradient(55%_40%_at_100%_8%,rgba(79,72,236,0.07),transparent_50%)]" />
          <div
            className="absolute inset-0 opacity-[0.32]"
            style={{
              backgroundImage: "radial-gradient(rgba(16,14,52,0.08) 1px, transparent 1px)",
              backgroundSize: "26px 26px",
            }}
          />
        </div>

        <div className="relative mx-auto max-w-8xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <motion.header
            className="max-w-3xl"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fade}
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40">
              What we do
            </p>
            <h2
              id="what-heading"
              className="mt-4 font-semibold leading-[1.08] tracking-tight text-navy [font-size:clamp(2rem,5vw,3.75rem)]"
            >
              Connecting every stage of
              <span className="mt-1 block">the financial journey</span>
            </h2>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-navy/60 sm:text-lg">
              Unlike traditional fintech companies that solve only one problem, SimFinance connects every
              stage of the financial customer lifecycle through one intelligent platform.
            </p>
          </motion.header>

          <div className="mt-12 grid gap-px overflow-hidden border border-navy/10 bg-navy/10 lg:grid-cols-2">
            <AudiencePanel
              index="01"
              title="Consumers"
              lede="Our ecosystem enables consumers to:"
              items={CONSUMER_ENABLE}
              fade={fade}
            />
            <AudiencePanel
              index="02"
              title="Financial institutions"
              lede="At the same time, financial institutions can:"
              items={INSTITUTION_ENABLE}
              fade={fade}
              dark
            />
          </div>

          <p className="mt-8 max-w-2xl text-base leading-relaxed text-navy/55">
            Everything is connected through one ecosystem designed for smarter financial services.
          </p>
        </div>
      </section>

      <section
        id="ecosystem"
        className="relative scroll-mt-32 overflow-hidden bg-[#f7f6fb] text-navy"
        aria-labelledby="ecosystem-heading"
      >
        <div className="pointer-events-none absolute inset-0" aria-hidden="true">
          <div className="absolute inset-0 bg-[radial-gradient(70%_50%_at_100%_0%,rgba(255,191,24,0.12),transparent_55%),radial-gradient(55%_40%_at_0%_20%,rgba(79,72,236,0.07),transparent_50%)]" />
          <div
            className="absolute inset-0 opacity-[0.36]"
            style={{
              backgroundImage: "radial-gradient(rgba(16,14,52,0.08) 1px, transparent 1px)",
              backgroundSize: "26px 26px",
            }}
          />
        </div>

        <div className="relative mx-auto max-w-8xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <motion.header
            className="max-w-3xl"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fade}
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40">
              Our ecosystem
            </p>
            <h2
              id="ecosystem-heading"
              className="mt-4 font-semibold leading-[1.08] tracking-tight text-navy [font-size:clamp(2rem,5vw,3.75rem)]"
            >
              One platform.
              <span className="mt-1 block">Three intelligent solutions.</span>
            </h2>
          </motion.header>

          <div className="mt-14 space-y-6">
            {PRODUCTS.map((product) => (
              <ProductCard key={product.id} product={product} fade={fade} />
            ))}
          </div>
        </div>
      </section>

      <section
        id="journey"
        className="relative scroll-mt-32 overflow-hidden bg-white text-navy"
        aria-labelledby="journey-heading"
      >
        <div className="pointer-events-none absolute inset-0" aria-hidden="true">
          <div className="absolute inset-0 bg-[radial-gradient(70%_50%_at_50%_0%,rgba(255,191,24,0.12),transparent_55%),radial-gradient(55%_40%_at_50%_100%,rgba(79,72,236,0.07),transparent_50%)]" />
          <div
            className="absolute inset-0 opacity-[0.32]"
            style={{
              backgroundImage: "radial-gradient(rgba(16,14,52,0.08) 1px, transparent 1px)",
              backgroundSize: "26px 26px",
            }}
          />
        </div>

        <div className="relative mx-auto max-w-8xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <motion.header
            className="max-w-3xl"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fade}
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40">
              How our ecosystem works
            </p>
            <h2
              id="journey-heading"
              className="mt-4 font-semibold leading-[1.08] tracking-tight text-navy [font-size:clamp(2rem,5vw,3.75rem)]"
            >
              The complete financial
              <span className="mt-1 block">customer journey</span>
            </h2>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-navy/60 sm:text-lg">
              Our integrated ecosystem transforms the complete financial customer journey—from first
              discovery to long-term engagement.
            </p>
          </motion.header>

          <JourneyBoard step={step} setStep={setStep} reduce={reduce} fade={fade} />
        </div>
      </section>
    </>
  );
}

function JourneyBoard({ step, setStep, reduce, fade }) {
  const active = JOURNEY_STEPS[step];
  const Icon = STEP_ICONS[active.icon] || TrendingUp;
  const last = JOURNEY_STEPS.length - 1;
  const go = (index) => setStep(Math.max(0, Math.min(last, index)));

  const onTrackKeyDown = (event) => {
    if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      event.preventDefault();
      go(step + 1);
    }
    if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      event.preventDefault();
      go(step - 1);
    }
    if (event.key === "Home") {
      event.preventDefault();
      go(0);
    }
    if (event.key === "End") {
      event.preventDefault();
      go(last);
    }
  };

  return (
    <motion.div
      className="mt-12"
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: "-80px" }}
      variants={fade}
    >
      <div className="overflow-x-auto pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <div className="min-w-[46rem] lg:min-w-0">
          <div className="grid grid-cols-7 gap-px overflow-hidden border border-navy/10 bg-navy/10">
            {JOURNEY_PHASES.map((phase) => {
              const start = JOURNEY_STEPS.findIndex((item) => item.phase === phase.id);
              const current = active.phase === phase.id;
              return (
                <button
                  key={phase.id}
                  type="button"
                  onClick={() => go(start)}
                  className={`cursor-pointer px-3 py-3 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-gold ${
                    current ? "bg-[#fffaf0]" : "bg-white hover:bg-[#faf8f1]"
                  } ${phase.span}`}
                  aria-current={current ? "true" : undefined}
                >
                  <p className="font-mono text-[10px] font-semibold tracking-[0.18em] text-gold">
                    {phase.range}
                  </p>
                  <p className="mt-1 text-sm font-semibold tracking-tight text-navy">{phase.label}</p>
                </button>
              );
            })}
          </div>

          <LayoutGroup id="about-journey">
            <div className="relative mt-8">
              <span
                className="pointer-events-none absolute left-[calc(100%/14)] right-[calc(100%/14)] top-5 h-px bg-navy/10"
                aria-hidden="true"
              />
              <motion.span
                className="pointer-events-none absolute left-[calc(100%/14)] top-5 h-px origin-left bg-gradient-to-r from-gold via-royal to-gold"
                aria-hidden="true"
                initial={false}
                animate={{ width: `calc(${step} * 100% / 7)` }}
                transition={reduce ? { duration: 0 } : { duration: 0.45, ease: EASE }}
              />

              <ol
                className="relative grid grid-cols-7"
                aria-label="Ecosystem steps"
                onKeyDown={onTrackKeyDown}
              >
                {JOURNEY_STEPS.map((item, index) => {
                  const selected = index === step;
                  const passed = index < step;
                  const StepIcon = STEP_ICONS[item.icon] || TrendingUp;

                  return (
                    <li key={item.n} className="min-w-0">
                      <button
                        type="button"
                        onClick={() => go(index)}
                        className="group flex w-full cursor-pointer flex-col items-center px-1 text-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-4"
                        aria-current={selected ? "step" : undefined}
                      >
                        <span
                          className={`relative z-[1] flex h-10 w-10 items-center justify-center rounded-full border text-[11px] font-semibold transition ${
                            selected
                              ? "border-gold bg-gold text-navy shadow-[0_10px_24px_rgba(255,191,24,0.35)]"
                              : passed
                                ? "border-royal bg-white text-royal"
                                : "border-navy/15 bg-white text-navy/40"
                          }`}
                        >
                          {item.n}
                          {selected ? (
                            <motion.span
                              layoutId="journey-station"
                              className="pointer-events-none absolute -inset-1 rounded-full border border-gold/50"
                              transition={reduce ? { duration: 0 } : { type: "spring", stiffness: 380, damping: 32 }}
                            />
                          ) : null}
                        </span>
                        <span
                          className={`mt-3 inline-flex h-8 w-8 items-center justify-center ${
                            selected ? "text-navy" : "text-navy/30"
                          }`}
                        >
                          <StepIcon className="h-4 w-4" aria-hidden="true" />
                        </span>
                        <span
                          className={`mt-1 min-h-[2.6rem] text-[13px] font-semibold leading-snug tracking-tight ${
                            selected ? "text-navy" : "text-navy/45 group-hover:text-navy"
                          }`}
                        >
                          {item.title}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ol>
            </div>
          </LayoutGroup>
        </div>
      </div>

      <article className="relative mt-10 overflow-hidden border border-navy/10 bg-navy text-white">
        <div className="pointer-events-none absolute -right-16 top-0 h-48 w-48 rounded-full bg-royal/30 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-16 left-8 h-40 w-40 rounded-full bg-gold/10 blur-3xl" />
        <p
          aria-hidden="true"
          className="pointer-events-none absolute -right-2 top-4 select-none font-black leading-none tracking-tight text-white/[0.06] [font-size:clamp(5rem,14vw,9rem)]"
        >
          {active.n}
        </p>

        <div className="relative grid gap-8 px-6 py-8 sm:px-8 sm:py-10 lg:grid-cols-[minmax(0,0.7fr)_minmax(0,1.3fr)] lg:items-center lg:px-12 lg:py-12">
          <div>
            <p className="font-mono text-[11px] font-semibold tracking-[0.22em] text-gold">
              {active.n} / 07
            </p>
            <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-white/40">
              {active.product}
            </p>
            <div className="mt-5 flex h-14 w-14 items-center justify-center bg-gold text-navy">
              <Icon className="h-6 w-6" aria-hidden="true" />
            </div>
          </div>

          <motion.div
            key={active.n}
            initial={reduce ? false : { opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={reduce ? { duration: 0 } : { duration: 0.35, ease: EASE }}
          >
            <h3 className="text-3xl font-semibold tracking-tight sm:text-[2.15rem]">{active.title}</h3>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-white/70 sm:text-lg">{active.copy}</p>
            <p className="mt-5 max-w-2xl text-sm leading-relaxed text-white/45">
              Together, these solutions create an end-to-end ecosystem that improves customer acquisition,
              risk management and portfolio performance.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => go(step - 1)}
                disabled={step === 0}
                className="inline-flex min-h-11 cursor-pointer items-center rounded-full border border-white/15 px-5 text-sm font-semibold text-white transition hover:border-white hover:bg-white hover:text-navy focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold disabled:cursor-not-allowed disabled:opacity-30"
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() => go(step + 1)}
                disabled={step === last}
                className="inline-flex min-h-11 cursor-pointer items-center rounded-full bg-gold px-5 text-sm font-semibold text-navy transition hover:bg-[#ffd056] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold disabled:cursor-not-allowed disabled:opacity-30"
              >
                Next stage
              </button>
              <a
                href={active.href}
                className="inline-flex min-h-11 cursor-pointer items-center gap-2 px-2 text-sm font-semibold text-white/70 transition hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold"
              >
                Open {active.product}
                <ArrowIcon className="h-3.5 w-3.5 text-gold" />
              </a>
            </div>
          </motion.div>
        </div>
      </article>
    </motion.div>
  );
}

function AudiencePanel({ index, title, lede, items, fade, dark = false }) {
  return (
    <motion.article
      className={`px-6 py-8 sm:px-8 sm:py-10 ${dark ? "bg-navy text-white" : "bg-white text-navy"}`}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: "-80px" }}
      variants={fade}
    >
      <p className={`font-mono text-[11px] font-semibold tracking-[0.2em] ${dark ? "text-gold" : "text-gold"}`}>
        {index}
      </p>
      <h3 className="mt-4 text-2xl font-semibold tracking-tight">{title}</h3>
      <p className={`mt-2 text-sm ${dark ? "text-white/55" : "text-navy/50"}`}>{lede}</p>
      <ul className="mt-6 space-y-3">
        {items.map((item) => (
          <li key={item} className="flex gap-3">
            <span
              className={`mt-2 h-1.5 w-1.5 shrink-0 ${dark ? "bg-gold" : "bg-gold"}`}
              aria-hidden="true"
            />
            <span className={`text-[15px] leading-relaxed ${dark ? "text-white/80" : "text-navy/75"}`}>
              {item}
            </span>
          </li>
        ))}
      </ul>
    </motion.article>
  );
}

function ProductCard({ product, fade }) {
  const dark = product.tone === "dark";

  return (
    <motion.article
      className={`relative grid overflow-hidden border lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)] ${
        dark
          ? "border-white/10 bg-navy text-white"
          : "border-navy/10 bg-white text-navy shadow-[0_24px_60px_-36px_rgba(16,14,52,0.28)]"
      }`}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: "-80px" }}
      variants={fade}
    >
      <div className="relative flex flex-col justify-center px-6 py-10 sm:px-10 lg:px-12 lg:py-12">
        {dark ? (
          <>
            <div className="pointer-events-none absolute -right-16 top-0 h-48 w-48 rounded-full bg-royal/30 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-16 left-8 h-40 w-40 rounded-full bg-gold/10 blur-3xl" />
          </>
        ) : null}
        <div className="relative flex items-center gap-3">
          <span className={`font-mono text-xs font-semibold tracking-[0.22em] ${dark ? "text-gold" : "text-navy/35"}`}>
            {product.index} / 03
          </span>
          <span className={`h-px w-8 ${dark ? "bg-gold/50" : "bg-navy/15"}`} />
          <span className={`text-[11px] font-semibold uppercase tracking-[0.16em] ${dark ? "text-white/40" : "text-navy/35"}`}>
            {product.role}
          </span>
        </div>
        <img
          src={product.logo}
          alt=""
          className="relative mt-6 h-10 w-auto max-w-[14rem] object-contain object-left sm:h-11"
        />
        <h3 className="relative mt-5 max-w-lg text-2xl font-semibold tracking-tight sm:text-[1.85rem]">
          {product.tagline}
        </h3>
        <p className={`relative mt-4 max-w-xl text-[15px] leading-[1.75] ${dark ? "text-white/65" : "text-navy/58"}`}>
          {product.copy}
        </p>
        <ul className="relative mt-7 flex flex-wrap gap-2">
          {product.capabilities.map((item) => (
            <li
              key={item}
              className={`border px-3 py-1.5 text-[12px] font-medium ${
                dark ? "border-white/15 text-white/80" : "border-navy/10 bg-[#f7f6fb] text-navy/75"
              }`}
            >
              {item}
            </li>
          ))}
        </ul>
        <a
          href={product.href}
          className={`relative mt-8 inline-flex min-h-11 w-fit cursor-pointer items-center gap-2 rounded-full px-5 text-sm font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
            dark
              ? "bg-gold text-navy hover:bg-[#ffd056] focus-visible:ring-gold focus-visible:ring-offset-navy"
              : "bg-navy text-white hover:bg-royal focus-visible:ring-navy"
          }`}
        >
          Open {product.name}
          <ArrowIcon />
        </a>
      </div>

      <div className={`relative min-h-[16rem] ${dark ? "bg-white/[0.04]" : "bg-[#f4f3f9]"}`}>
        <MediaSlot
          src={product.art}
          alt={`${product.name} product illustration`}
          label={`${product.index}  ·  ${product.name}`}
          spec={`Product illustration · 1600×1200 landscape. File already used on the homepage solutions chapter for ${product.name}.`}
          className="absolute inset-4 h-[calc(100%-2rem)] w-[calc(100%-2rem)]"
          tone={dark ? "dark" : "light"}
          fit="contain"
          position="object-center"
        />
      </div>
    </motion.article>
  );
}

export default AboutPlatform;
